import { useState, useEffect } from "react"
import { Clock, CheckCircle, Pencil, Save, X, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modules/shared/ui/components/select"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import type { WorkflowDto } from "@/modules/onboarding/workflows/dtos/workflow.dto"
import type { OffboardingWorkflowDto } from "@/modules/offboarding/dtos"

interface Props {
  offboardingId: string
  organizationId: string
  workflows: OffboardingWorkflowDto[]
  availableWorkflows: WorkflowDto[]
  isTerminal: boolean
  onRefresh: () => void
}

export function OffboardingWorkflowsCard({
  offboardingId,
  organizationId,
  workflows,
  availableWorkflows,
  isTerminal,
  onRefresh,
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [workflowIds, setWorkflowIds] = useState<string[]>([])

  useEffect(() => {
    setWorkflowIds(workflows?.map((w) => w.id) ?? [])
  }, [workflows])

  const handleSave = async () => {
    const payload = { workflowTemplateIds: workflowIds }
    setIsSaving(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, payload)
      toast.success("Workflows updated")
      setIsEditing(false)
      onRefresh()
    } catch {
      toast.error("Failed to update workflows")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="bg-card border border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-foreground">Workflows</h2>
          {isTerminal ? null : !isEditing ? (
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white border-0" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          )}
        </div>
        {!isEditing ? (
          workflows && workflows.length > 0 ? (
            <div className="space-y-2">
              {workflows.map((wf) => (
                <div key={wf.id} className="rounded-md border border-border bg-muted p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium text-foreground text-sm">{wf.name}</p>
                    <Badge className={(wf.status ?? "").toLowerCase() === "completed" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : (wf.status ?? "").toLowerCase() === "inprogress" ? "bg-blue-100 text-blue-700 border-blue-200"
                      : "bg-muted text-muted-foreground border-border"}>
                      {wf.status ?? "Unknown"}
                    </Badge>
                  </div>
                  {wf.description && <p className="text-xs text-muted-foreground mt-1">{wf.description}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />Started: {new Date(wf.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    {wf.completedAt && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" />Completed: {new Date(wf.completedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No workflows attached.</p>
        ) : (
          <div className="space-y-3">
            <Select value="" onValueChange={(wId) => { if (!workflowIds.includes(wId)) setWorkflowIds((p) => [...p, wId]) }}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Add workflow" /></SelectTrigger>
              <SelectContent className="bg-card">
                {availableWorkflows.map((w) => <SelectItem key={w.id} value={w.id} className="hover:bg-muted cursor-pointer">{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {workflowIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {workflowIds.map((wId) => {
                  const wf = availableWorkflows.find((w) => w.id === wId)
                  return wf ? (
                    <div key={wId} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                      <span>{wf.name}</span>
                      <button type="button" onClick={() => setWorkflowIds((p) => p.filter((i) => i !== wId))}><X className="h-3 w-3" /></button>
                    </div>
                  ) : null
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No workflows selected. Save with no workflows to remove all.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
